import { ReactNode, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styles from "./index.module.scss";
import TextInput from "../TextInput";
import ToggleButton from "../ToggleButton";
import { useTriggerOnFieldChange } from "../../hooks/useTriggerOnFieldChange";
import Checkbox from "../Checkbox";
import { numbersWithDecimals } from "../../utils/forms/validation";

type PropsType = {
  required?: boolean;
  formSection: string;
  formFieldId: string;
  formFieldUnitsId?: string; // Optional, to support unfortunate inconsistent field:unitField naming
  label: string;
  type: "weight" | "length";
  valuePrefix: string;
  maxLength?: number;
  tooltip?: ReactNode;
  naOption?: boolean;
  readOnly?: boolean;
};

const MeasurementInput = ({
  required = false,
  formSection,
  formFieldId,
  formFieldUnitsId,
  label,
  type,
  valuePrefix,
  maxLength,
  tooltip,
  naOption = false,
  readOnly = false,
}: PropsType) => {
  const { getValues, setValue } = useFormContext();
  const formNaKey = `${formSection}.${formFieldId}-na`;
  const formNaValue = useWatch({ name: formNaKey });
  const na = naOption && formNaValue;

  const units = {
    weight: ["kg", "lbs"],
    length: ["cm", "in"],
  };

  const formFieldUnitsName = formFieldUnitsId || `${formFieldId}-units`;
  const formUnitsKey = `${formSection}.${formFieldUnitsName}`;
  const formKey = `${formSection}.${formFieldId}`;

  // Blank the input if NA is selected
  const formUnitsValue = getValues(formUnitsKey);
  const formValue = getValues(formKey);
  useEffect(() => {
    if (na && (formValue || formUnitsValue)) {
      setValue(formKey, "");
      setValue(formUnitsKey, "");
    }
  }, [na, formValue, formUnitsValue, setValue, formKey, formUnitsKey]);

  // Update validation when necessary
  useTriggerOnFieldChange(formNaKey, formKey);
  useTriggerOnFieldChange(formUnitsKey, formKey);

  return (
    <div className={styles.gridInputToggle}>
      <TextInput
        section={formSection}
        id={formFieldId}
        label={label}
        placeholder=""
        maxLength={maxLength || 25}
        fieldOptions={{
          required: required && !na && "This field is required.",
          validate: {
            units: () => {
              const value = getValues(formKey);
              const unit = getValues(formUnitsKey);
              return value && !unit
                ? `Please select ${units[type].join(" or ")}`
                : undefined;
            },
          },
          pattern: numbersWithDecimals,
        }}
        tooltip={tooltip}
        disabled={na}
        readOnly={readOnly}
      />
      <ToggleButton
        section={formSection}
        name={formFieldUnitsName}
        leftOption={{
          label: units[type][0],
          value: `${valuePrefix}-${units[type][0]}`,
        }}
        rightOption={{
          label: units[type][1],
          value: `${valuePrefix}-${units[type][1]}`,
        }}
        className={styles.toggle}
        readOnly={na || readOnly}
      />
      {naOption && (
        <Checkbox
          name={formNaKey}
          value={`${valuePrefix}-na`}
          label="N/A"
          readOnly={readOnly}
          className={styles.naOption}
        />
      )}
    </div>
  );
};

export default MeasurementInput;
